import type { Theme } from '@mapbox/search-js-web'
import { Field, FieldDescription, FieldError, FieldLabel } from '@repo/shared-client/components/ui'
import { Controller, type RegisterOptions, useFormContext } from '@repo/shared-client/form'
import { cn, mergeRefs } from '@repo/shared-client/lib/utils'
import { MapBoxSearchBox } from '@repo/shared-client/mapbox'
import { useMapBox } from '@web/src/hooks/use-mapbox'
import type { ComponentProps, ComponentRef, ReactNode } from 'react'
import { forwardRef } from 'react'

export type SFLocationSearchFieldProps = Omit<ComponentProps<typeof Field>, 'children'> & {
  name: string
  rules?: RegisterOptions
  placeholder?: string
  enableGeolocation?: boolean
  locationTypes?: string
  onLocationChange?: (location: { name: string; longitude: number; latitude: number }) => void
  label?: ReactNode
  labelEnd?: ReactNode
  helperText?: ReactNode
  optionalText?: ReactNode
  required?: boolean
  theme?: Theme
}

const SFLocationSearchField = forwardRef<ComponentRef<typeof MapBoxSearchBox>, SFLocationSearchFieldProps>(
  (props, forwardedRef) => {
    const {
      formState: { errors },
    } = useFormContext()
    const { mapBoxToken } = useMapBox()
    const {
      name,
      rules,
      placeholder,
      enableGeolocation,
      locationTypes = 'place',
      onLocationChange,
      label,
      labelEnd,
      helperText,
      optionalText,
      required,
      className,
      ...fieldRest
    } = props

    const error = errors[name]
    const invalid = !!error

    return (
      <Field className={cn('inline-grid w-full', className)} data-invalid={invalid} {...fieldRest}>
        {label !== undefined ? (
          <div className={cn('flex items-center gap-1', labelEnd != null && 'w-full')}>
            <FieldLabel className="flex items-center gap-1">
              {label}
              {required ? (
                <span aria-hidden className="text-destructive">
                  *
                </span>
              ) : optionalText ? (
                <span className="font-normal text-muted-foreground text-sm">{optionalText}</span>
              ) : null}
            </FieldLabel>
            {labelEnd}
          </div>
        ) : null}
        <Controller
          name={name}
          render={({ field }) => (
            <MapBoxSearchBox
              {...field}
              accessToken={mapBoxToken}
              enableGeolocation={enableGeolocation}
              locationTypes={locationTypes}
              onChange={(e) => {
                field.onChange({
                  name: e,
                  longitude: null,
                  latitude: null,
                })
              }}
              onClear={() => {
                field.onChange({
                  name: '',
                  longitude: null,
                  latitude: null,
                })
              }}
              onRetrieve={(e) => {
                const feature = e.features[0]
                if (!feature) {
                  return
                }
                field.onChange({
                  name: feature.properties.full_address,
                  longitude: feature.geometry.coordinates[0],
                  latitude: feature.geometry.coordinates[1],
                })
                onLocationChange?.({
                  name: feature.properties.full_address,
                  longitude: feature.geometry.coordinates[0],
                  latitude: feature.geometry.coordinates[1],
                })
              }}
              placeholder={placeholder}
              ref={mergeRefs(field.ref, forwardedRef)}
              theme={props.theme}
              value={field?.value?.name}
            />
          )}
          rules={rules}
        />
        {helperText !== undefined ? <FieldDescription>{helperText}</FieldDescription> : null}
        {error?.message ? <FieldError>{String(error.message)}</FieldError> : null}
      </Field>
    )
  }
)

SFLocationSearchField.displayName = 'SFLocationSearchField'

export default SFLocationSearchField
